import React, { FC, useState } from 'react';
import useTheme from 'evcharging/hooks/useTheme';
import FindProgramAccordion from 'evcharging/containers/ev-rebates/FindProgramAccordion';
import { css } from '@emotion/css';
import { Theme } from 'evcharging/app/themes';
import Paragraph from 'evcharging/components/Paragraph';
import Box from 'evcharging/components/Box';
import useI18n from 'evcharging/hooks/useI18n';

export interface FindProgramAccordionItemProps {
  title: string;
  body: FindProgramAccordionItemProps[];
  header?: string;
  nested?: boolean;
}

const FindProgramAccordionItem: FC<FindProgramAccordionItemProps> = (props) => {
  const { header, title, body } = props;
  const [open, setOpen] = useState<boolean>(false);
  const contained = Array.isArray(body);
  const theme = useTheme();
  const styles = createStyles(theme, { open, contained, ...props });
  const onToggle = () => setOpen(!open);
  const { t } = useI18n();

  return (
    <div className={styles.root}>
      <h4 className={styles.header}>{header ? t(header) : ''}</h4>
      <button className={styles.button} type="button" onClick={onToggle}>
        {title}
        <span className={styles.icon}></span>
      </button>
      <div className={styles.body}>
        {contained ? (
          <Box background={'#eef0f2'} padding={'0 20px'} borderTop={'1px solid #b0babf'}>
            <FindProgramAccordion items={body} nested={true} />
          </Box>
        ) : (
          <Paragraph>{body}</Paragraph>
        )}
      </div>
    </div>
  );
};

const createStyles = (
  theme: Theme,
  {
    open,
    nested = false,
    contained = false,
  }: FindProgramAccordionItemProps & { open: boolean; contained: boolean }
) => ({
  root: css`
    padding: 20px 0;
    ${contained && open && 'padding-bottom: 0;'}
    border-bottom: 1px solid #b0babf;

    :last-child {
      ${nested && 'border-bottom: none;'}
    }

    ${theme.breakpoints.mobile.down} {
      padding: 16px 0;
    }
  `,
  button: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: auto;
    width: 100%;
    border: none;
    padding: 0;
    text-align: left;
    background: transparent;
    outline: none;
    font-weight: 700;
    font-size: ${nested ? '20px' : '24px'};
    line-height: 32px;
    color: #252c32;
    text-transform: capitalize;
    cursor: pointer;

    ${theme.breakpoints.mobile.down} {
      font-size: ${nested ? '18px' : '20px'};
    }
  `,
  body: css`
    height: 0;
    opacity: 0;
    visibility: hidden;
    ${open &&
    `
    height: auto;
    opacity: 1;
    visibility: visible;
    padding-top: 24px;
    `}
  `,
  icon: css`
    height: 17px;
    width: 30px;
    margin-top: 5px;
    margin-left: 20px;
    background: ${open
        ? `url("data:image/svg+xml,%3Csvg width='18' height='10' viewBox='0 0 18 10' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M17.3984 7.82422L9.93357 0.671875C9.68357 0.460938 9.44919 0.374999 9.24997 0.374999C9.05075 0.374999 8.7824 0.461679 8.60193 0.636249L1.10193 7.82422C0.727083 8.17969 0.714895 8.80859 1.0738 9.14844C1.43025 9.52441 2.02614 9.53664 2.39841 9.17651L9.24997 2.60938L16.1015 9.17187C16.4726 9.53199 17.0695 9.51977 17.4261 9.1438C17.7851 8.80859 17.7734 8.17969 17.3984 7.82422Z' fill='%2384919A'/%3E%3C/svg%3E%0A")`
        : `url("data:image/svg+xml,%3Csvg width='18' height='10' viewBox='0 0 18 10' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M17.3984 2.17578L9.93357 9.32812C9.68357 9.53906 9.44919 9.625 9.24997 9.625C9.05075 9.625 8.7824 9.53832 8.60193 9.36375L1.10193 2.17578C0.727083 1.82031 0.714895 1.19141 1.0738 0.851563C1.43025 0.475586 2.02614 0.46336 2.39841 0.823489L9.24997 7.39062L16.1015 0.828125C16.4726 0.468008 17.0695 0.480235 17.4261 0.8562C17.7851 1.19141 17.7734 1.82031 17.3984 2.17578Z' fill='%236E7C87'/%3E%3C/svg%3E%0A")`}
      center no-repeat;
  `,
  header: css`
    color: #6e7c87;
    font-weight: 400;
    font-size: 20px;
    line-height: 32px;
    margin-bottom: 18px;
    ${theme.breakpoints.mobile.down} {
      font-size: 18px;
      margin-bottom: 8px;
    }
  `,
});

export default FindProgramAccordionItem;
